import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { AuthEntity } from '_entities';
import { FE_BUILD_VERSION } from '_constants';
import { useBeVersion } from 'utils/services';
import { isEmpty } from 'lodash';
import * as Styled from './styles';
import CollapsibleTable from './CollapsibleTable';

const {
  getDetailedPermissions,
  getModularityPermissions,
  getTimeZone,
  getRoleName,
  getRoleAccountName,
  getUserEmail,
  getAgenciesList,
  getSecGeogList,
  getUserStateName,
} = AuthEntity.selectors;

const UserInformationModal = ({ isOpen, onClose }) => {
  const detailedUserPermisiion = useSelector(getDetailedPermissions);
  const modularityPermissions = useSelector(getModularityPermissions);
  const timeZone = useSelector(getTimeZone);
  const userRole = useSelector(getRoleName);
  const roleAccount = useSelector(getRoleAccountName);
  const userEmail = useSelector(getUserEmail);
  const userAgencies = useSelector(getAgenciesList);
  const userSecGeogs = useSelector(getSecGeogList);
  const accountState = useSelector(getUserStateName);
  const beVersion = useBeVersion();
  const [expandedSections, setExpandedSections] = React.useState({
    permissions: false,
    modularityPermissions: false,
    geographies: false,
    agencies: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Modularity permissions come as bare codenames (account modules + the per-module view permissions the
  // profile serializer builds), so there is no name to pair them with as in the role permissions above.
  const formatedModularityPermissions = useMemo(() => {
    if (isEmpty(modularityPermissions)) return [];
    return modularityPermissions
      .map((permission) => (permission && typeof permission === 'object' ? permission.codename : permission))
      .filter(Boolean)
      .sort();
  }, [modularityPermissions]);

  const formatedSecGeogs = useMemo(() => {
    if (isEmpty(userSecGeogs)) return [];
    return userSecGeogs.map(({ attributes: { name }, id }) => ({ name, id }));
  }, [userSecGeogs]);

  const formatedUserAgencies = useMemo(() => {
    if (isEmpty(userAgencies)) return [];
    return userAgencies.map(({ attributes: { name }, id }) => ({ name, id }));
  }, [userAgencies]);

  // Every account has its own role of each type, so name the role's account: "Multi-Account Manager
  // (Arizona WSU)" — a manager signed into another account still holds their home account's role.
  const roleLabel = userRole && roleAccount ? `${userRole} (${roleAccount})` : userRole;

  if (!isOpen) return null;

  return (
    <Styled.ModalOverlay onClick={onClose}>
      <Styled.ModalContent onClick={(e) => e.stopPropagation()}>
        <Styled.ModalHeader>
          <Styled.ModalTitle>User Profile Information</Styled.ModalTitle>
          <Styled.ModalCloseButton type="button" onClick={onClose} aria-label="Close" />
        </Styled.ModalHeader>

        <Styled.ModalBody>
          {/* User Info Card */}
          <Styled.InfoCard>
            <Styled.InfoRow>
              <Styled.InfoLabel>Email:</Styled.InfoLabel>
              <Styled.InfoValue>{userEmail || 'N/A'}</Styled.InfoValue>
            </Styled.InfoRow>
            <Styled.InfoRow>
              <Styled.InfoLabel>Account State:</Styled.InfoLabel>
              <Styled.InfoValue>{accountState || 'N/A'}</Styled.InfoValue>
            </Styled.InfoRow>
            <Styled.InfoRow>
              <Styled.InfoLabel>Role:</Styled.InfoLabel>
              <Styled.InfoValue>{roleLabel || 'N/A'}</Styled.InfoValue>
            </Styled.InfoRow>
            <Styled.InfoRow>
              <Styled.InfoLabel>Time Zone:</Styled.InfoLabel>
              <Styled.InfoValue>{timeZone || 'N/A'}</Styled.InfoValue>
            </Styled.InfoRow>
            <Styled.InfoRow>
              <Styled.InfoLabel>FE Version:</Styled.InfoLabel>
              <Styled.InfoValue>{FE_BUILD_VERSION}</Styled.InfoValue>
            </Styled.InfoRow>
            <Styled.InfoRow>
              <Styled.InfoLabel>BE Version:</Styled.InfoLabel>
              <Styled.InfoValue>{beVersion || 'N/A'}</Styled.InfoValue>
            </Styled.InfoRow>
          </Styled.InfoCard>

          <CollapsibleTable
            title="Permissions"
            isExpanded={expandedSections.permissions}
            onToggle={() => toggleSection('permissions')}
            columns={['Name', 'Codename']}
            rows={(detailedUserPermisiion || []).map((p) => [p.name, p.codename])}
          />
          <CollapsibleTable
            title="Modularity Permissions"
            isExpanded={expandedSections.modularityPermissions}
            onToggle={() => toggleSection('modularityPermissions')}
            columns={['Codename']}
            rows={formatedModularityPermissions.map((codename) => [codename])}
          />
          <CollapsibleTable
            title="Geographies"
            isExpanded={expandedSections.geographies}
            onToggle={() => toggleSection('geographies')}
            columns={['Geography ID', 'Geography Name']}
            rows={formatedSecGeogs.map((g) => [g.id, g.name])}
          />
          <CollapsibleTable
            title="Agencies"
            isExpanded={expandedSections.agencies}
            onToggle={() => toggleSection('agencies')}
            columns={['Agency ID', 'Agency Name']}
            rows={formatedUserAgencies.map((a) => [a.id, a.name])}
          />
        </Styled.ModalBody>
      </Styled.ModalContent>
    </Styled.ModalOverlay>
  );
};

UserInformationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UserInformationModal;
